"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";

import { chatRepository } from "@/features/chat/api/chat-repository";
import type {
  ChatAttachment,
  SendMessageInput,
  StartConversationTarget,
} from "@/features/chat/types";

/**
 * React Query bindings for direct messaging.
 *
 * Deliberately thin: the *live* thread state (optimistic bubbles, retries,
 * socket reconciliation) is owned by the chat view, not the cache — a message
 * list that re-renders from a server round-trip on every keystroke-adjacent
 * event would fight the socket. These hooks cover the request/response edges:
 * the conversation list, a thread's first page, and the mutations.
 */

export function useConversations(page = 1) {
  return useQuery({
    queryKey: [...queryKeys.chat.conversations(), page],
    queryFn: () => chatRepository.listConversations(page),
  });
}

export function useMessages(conversationId: string) {
  return useQuery({
    queryKey: queryKeys.chat.messages(conversationId),
    queryFn: () => chatRepository.getMessages({ conversationId }),
    enabled: Boolean(conversationId),
  });
}

/**
 * Navbar badge. Polled rather than socket-driven so the count stays right on
 * pages that never mount the chat view.
 */
export function useUnreadCount(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.chat.unread(),
    queryFn: () => chatRepository.getUnreadCount(),
    enabled: options.enabled ?? true,
    refetchInterval: 45_000,
    refetchOnWindowFocus: true,
  });
}

export function useStartConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (target: StartConversationTarget) =>
      chatRepository.startConversation(target),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.chat.conversations() });
    },
  });
}

export function useClearConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) =>
      chatRepository.clearConversation(conversationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.chat.all });
    },
  });
}

/**
 * Sends and returns the server-authoritative message. The caller reconciles it
 * into the thread by `clientId`; we only refresh the conversation list preview.
 */
export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendMessageInput) => chatRepository.sendMessage(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.chat.conversations() });
    },
  });
}

export function useEditMessage() {
  return useMutation({
    mutationFn: ({ messageId, text }: { messageId: string; text: string }) =>
      chatRepository.editMessage(messageId, text),
  });
}

export function useDeleteMessage() {
  return useMutation({
    mutationFn: (messageId: string) => chatRepository.deleteMessage(messageId),
  });
}

export function useReactToMessage() {
  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: string }) =>
      chatRepository.reactToMessage(messageId, emoji),
  });
}

/**
 * Marking read is fire-and-forget from the thread's perspective, but it changes
 * the badge — so the unread query is invalidated, not the whole thread.
 */
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) =>
      chatRepository.markRead(conversationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.chat.unread() });
      void qc.invalidateQueries({ queryKey: queryKeys.chat.conversations() });
    },
  });
}

export function useUploadAttachments() {
  return useMutation({
    mutationFn: ({
      files,
      compress,
    }: {
      files: File[];
      compress: boolean;
    }): Promise<ChatAttachment[]> =>
      chatRepository.uploadAttachments(files, { compressed: compress }),
  });
}
